import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { IRole } from '@boma/administration-portal/shared/entities';
import * as RolesActions from './roles.actions';

export const ROLES_FEATURE_KEY = 'roles';

export interface RoleState extends EntityState<IRole> {
  selectedId?: string | number; // which Roles record has been selected
  loaded: boolean; // has the Roles list been loaded
  error?: string | null; // last known error (if any)
  page?: number | null;
  perPage?: number | null;
  query?: string | null;
}

export interface RolesPartialState {
  readonly [ROLES_FEATURE_KEY]: RoleState;
}

export const rolesAdapter: EntityAdapter<IRole> = createEntityAdapter<IRole>();

export const initialRoleState: RoleState = rolesAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  page: 1,
  perPage: 250,
  query: '',
});

const rolesReducer = createReducer(
  initialRoleState,
  on(RolesActions.loadRoles, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(RolesActions.loadRolesSuccess, (state, { roles }) =>
    rolesAdapter.setAll(roles.items, { ...state, loaded: true })
  ),
  on(RolesActions.loadRolesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(RolesActions.createRoleSuccess, (state, { role }) =>
    rolesAdapter.upsertOne(role, { ...state, loaded: true })
  ),
  on(RolesActions.createRoleFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(RolesActions.setSelectedRole, (state, action) => {
    return {
      ...state,
      selectedId: action.roleId,
    };
  }),
  on(RolesActions.loadRoleDetailsSuccess, (state, { role }) =>
    rolesAdapter.upsertOne(role, {
      ...state,
      loaded: true,
      error: '',
      selectedId: role.id,
    })
  ),
  on(RolesActions.loadRoleDetailsFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(RolesActions.updateRoleSuccess, (state, { role }) =>
    rolesAdapter.upsertOne(role, {
      ...state,
      loaded: true,
      error: '',
      selectedId: role.id,
    })
  ),
  on(RolesActions.updateRoleFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(RolesActions.deleteRoleSuccess, (state, { roleId }) =>
    rolesAdapter.removeOne(roleId, state)
  ),
  on(RolesActions.deleteRoleFailure, (state, { error }) => ({
    ...state,
    error,
  }))
);

export function RoleReducer(state: RoleState | undefined, action: Action) {
  return rolesReducer(state, action);
}
